import { downloadAction, printAction, uploadAction } from '../../../../utils/actions'
import { getAppEventName } from '../../../../utils/services/googleAnalytics'
import { appConfig } from '../../common/constants'
import { handleDataLayerEvent, handleGAEvent } from '../../common/googleAnalytics'
import useStaticData from '../../common/useStaticData'

const formatDisplayInput = (inputData, staticData, selectedFields) => {
  const formattedData = {}
  const {
    subApp,
    insertType,
    insertDiameter,
    waterFilledHoles,
    adhesiveAnchor,
    drillBitDiameter,
    numberInstallations,
    overageFactor,
    screenTube,
    embedmentDepth,
    crackWidth,
    crackDepth,
    crackLength,
    injectionResin,
    pasteOverEpoxy,
    pasteOverBothSidesOfCrack,
    pumpingPortStyle,
    pumpingPortSpacing,
  } = inputData
  const { dowelingOptions, crackInjectionOptions, subAppList } = staticData

  const { getInsertDiameterOptions, getDrillBitDiameterOptions, getScreenTubeOptions } =
    useStaticData(staticData)

  const insertDiameterOptions = getInsertDiameterOptions(insertType)
  const drillBitDiameterOptions = getDrillBitDiameterOptions(
    adhesiveAnchor,
    insertType,
    insertDiameter,
  )
  let overageFactorOptions = []
  if (subApp === '0') {
    overageFactorOptions = dowelingOptions.overageFactor
  } else {
    overageFactorOptions = crackInjectionOptions.overageFactor
  }
  const screenTubeOptions = getScreenTubeOptions(adhesiveAnchor, insertType, insertDiameter)
  const crackWidthOptions = crackInjectionOptions['crackWidth']
  const pumpingPortSpacingOptions = crackInjectionOptions['pumpingPortSpacing']

  const fieldMappings = {
    subApp: subAppList.find((item) => item.value == subApp)?.text,
    insertType: insertType,
    waterFilledHoles: waterFilledHoles === undefined ? undefined : waterFilledHoles ? 'Yes' : 'No',
    insertDiameter: insertDiameterOptions.find((item) => item.value == insertDiameter)?.text,
    adhesiveAnchor: adhesiveAnchor,
    drillBitDiameter: drillBitDiameterOptions.find((item) => item.value == drillBitDiameter)?.text,
    numberInstallations: numberInstallations,
    overageFactor: overageFactorOptions.find((item) => item.value === overageFactor)?.text,
    screenTube: screenTubeOptions.find((item) => item.value === screenTube)?.text,
    embedmentDepth: embedmentDepth,
    crackWidth: crackWidthOptions.find((item) => item.value === crackWidth)?.text,
    crackDepth: crackDepth,
    crackLength: crackLength,
    injectionResin: injectionResin,
    pasteOverEpoxy: pasteOverEpoxy,
    pasteOverBothSidesOfCrack:
      pasteOverBothSidesOfCrack === undefined
        ? undefined
        : pasteOverBothSidesOfCrack
          ? 'Yes'
          : 'No',
    pumpingPortStyle: pumpingPortStyle,
    pumpingPortSpacing: pumpingPortSpacingOptions.find((item) => item.value === pumpingPortSpacing)
      ?.text,
  }

  for (const field of selectedFields) {
    if (field in fieldMappings) {
      formattedData[field] = fieldMappings[field]
    }
  }

  return formattedData
}

export const generateGAInput = (appInput, staticData) => {
  let cloneAppInput = Object.assign({}, appInput)
  let cloneAppStaticData = Object.assign({}, staticData)
  let gaInput = {}
  let fields = []

  if (cloneAppInput.subApp === '0') {
    fields = [
      'subApp',
      'insertType',
      'waterFilledHoles',
      'insertDiameter',
      'adhesiveAnchor',
      'drillBitDiameter',
      'numberInstallations',
      'overageFactor',
      'screenTube',
      'embedmentDepth',
    ]
  } else {
    fields = [
      'subApp',
      'crackWidth',
      'crackDepth',
      'crackLength',
      'overageFactor',
      'injectionResin',
      'pasteOverEpoxy',
      'pasteOverBothSidesOfCrack',
      'pumpingPortStyle',
      'pumpingPortSpacing',
    ]
  }

  gaInput = formatDisplayInput(cloneAppInput, cloneAppStaticData, fields)

  return Object.entries(gaInput).reduce((result, [key, val]) => {
    result[key] = val !== undefined ? String(val) : 'N/A'
    return result
  }, {})
}

const generateAddJobInputGa = (job) => {
  let gaInput = Object.assign({}, job)
  delete gaInput.userInputs

  gaInput = {
    cartridges: Object.keys(gaInput)
      .filter((key) => key !== 'jobName')
      .join(','),
    quantities: Object.values(gaInput)
      .filter((value) => typeof value === 'number')
      .map((value) => value.toFixed(2))
      .join(','),
  }
  return gaInput
}

const gaEvents = (store) => (next) => (action) => {
  next(action)

  const {
    calculation: { input, output, status },
    calculationCI,
    calculationDoweling,
    staticData,
  } = store.getState()

  const appEventName = getAppEventName(appConfig.appCode, input.source === 'EC')

  switch (action.type) {
    case 'CALCULATION_SUCCESS':
    case 'CALCULATION_FAILURE': {
      handleGAEvent({
        appEventName,
        gaActionName: 'Calculation_Action',
        gaLabelName: 'Calculation',
        input,
        output: status,
      })

      if (!status.msgBody) {
        handleDataLayerEvent({
          appEventName,
          appAction: 'Calculation_Action',
          appInputs: generateGAInput(input, staticData),
          appOutputs: status,
        })
      }
      break
    }
    case uploadAction.type: {
      const {
        calculation: { input, output },
      } = action.payload

      const { jobs } = output

      handleGAEvent({
        appEventName,
        gaActionName: 'Upload_Action',
        gaLabelName: 'Upload',
        input: input,
        output: status,
      })

      handleDataLayerEvent({
        appEventName,
        appAction: 'Upload_Action',
        appInputs: generateGAInput(input, staticData),
        appOutputs: status,
      })

      if (jobs && jobs.length > 0) {
        // Wait for Google Tag to be flushed
        setTimeout(() => {
          jobs.forEach((job) => {
            handleGAEvent({
              appEventName,
              gaActionName: 'Add_New_Job_Action',
              gaLabelName: 'Add_New_Job',
              input: output,
            })

            handleDataLayerEvent({
              appEventName,
              appAction: 'AddToJobList_Action',
              appInputs: generateAddJobInputGa(job),
              appOutputs: status,
            })
          })
        })
      }

      break
    }
    case downloadAction.type: {
      handleGAEvent({
        appEventName,
        gaActionName: 'Download_Action',
        gaLabelName: 'Download',
        input: input,
        output: status,
      })

      handleDataLayerEvent({
        appEventName,
        appAction: 'Download_Action',
        appInputs: generateGAInput(input, staticData),
        appOutputs: status,
      })
      break
    }
    case printAction.type: {
      handleGAEvent({
        appEventName,
        gaActionName: 'Print_Action',
        gaLabelName: 'Print',
        input: input,
        output: status,
      })

      handleDataLayerEvent({
        appEventName,
        appAction: 'Print_Action',
        appInputs: generateGAInput(input, staticData),
        appOutputs: status,
      })
      break
    }
    case 'JOB_ADD': {
      let gaInput = generateAddJobInputGa(action.payload)

      handleGAEvent({
        appEventName,
        gaActionName: 'Add_New_Job_Action',
        gaLabelName: 'Add_New_Job',
        input: output,
      })

      handleDataLayerEvent({
        appEventName,
        appAction: 'AddToJobList_Action',
        appInputs: gaInput,
        appOutputs: status,
      })
      break
    }
    case 'SWITCH_SUBAPP': {
      /**
       * If CI or Doweling is not existed. GA will be handled in dowelingCalculation and crackInjectionCalculation
       */
      if (action.payload.subApp === '1') {
        if (calculationCI && calculationCI.input) {
          // if CI already existed
          if (!status.msgBody) {
            handleDataLayerEvent({
              appEventName,
              appAction: 'Calculation_Action',
              appInputs: generateGAInput(input, staticData),
              appOutputs: status,
            })
          }
        }
      } else {
        if (calculationDoweling && calculationDoweling.input) {
          // if Doweling already existed
          if (!status.msgBody) {
            handleDataLayerEvent({
              appEventName,
              appAction: 'Calculation_Action',
              appInputs: generateGAInput(input, staticData),
              appOutputs: status,
            })
          }
        }
      }
      break
    }
    default:
      break
  }
}

export default gaEvents
